import React, { useState } from "react";
import { CloudUpload, DeleteOutline, DriveFileRenameOutline, FileDownload } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { getCurrentProject } from "fond/project";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { ConfirmModal, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

export type LayerPanelAction = "draw" | "upload" | "export" | "delete";

interface LayerPanelProps {
  title: string;
  layerKey: string;
  testIdPrefix?: string;
  actions?: LayerPanelAction[];
  drawTitle?: string;
  drawSubtitle?: string;
  onDelete?: () => void | Promise<void>;
  canDelete?: boolean;
  deleteConfirmHeader?: string;
  deleteConfirmContent?: React.ReactNode;
}

const DEFAULT_ACTIONS: LayerPanelAction[] = ["draw", "upload", "export"];

const LayerPanel: React.FC<LayerPanelProps> = ({
  title,
  layerKey,
  testIdPrefix,
  actions = DEFAULT_ACTIONS,
  drawTitle = "Draw",
  drawSubtitle = "Add and edit data on the map",
  onDelete,
  canDelete = false,
  deleteConfirmHeader,
  deleteConfirmContent,
}) => {
  const { open, clear } = useStackedNavigationContext();
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const canViewExport = permissionCheck(project?.Permission?.Level, Actions.PROJECT_EXPORT_VIEW);
  const testPrefix = testIdPrefix || layerKey;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOnDrawButtonClick = () => {
    open(`${layerKey}-draw`);
  };

  const handleOnUploadPolygon = () => {
    open(`${layerKey}-upload`);
  };

  const handleOnExportLayer = () => {
    open(`${layerKey}-export`);
  };

  const handleOnDeleteButtonClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
      clear();
    } finally {
      setIsDeleting(false);
    }
  };

  const onBack = () => {
    clear();
  };

  return (
    <Box>
      <StackedNavigationHeader title={title} onBackButtonClick={onBack} />
      {actions.includes("draw") && (
        <StackedNavigationButton
          data-testid={`draw-${testPrefix}-button`}
          icon={<DriveFileRenameOutline />}
          title={drawTitle}
          subtitle={drawSubtitle}
          onClick={handleOnDrawButtonClick}
        />
      )}
      {actions.includes("upload") && (
        <StackedNavigationButton
          data-testid={`upload-${testPrefix}-button`}
          icon={<CloudUpload />}
          title="Upload"
          subtitle="Use my data"
          onClick={handleOnUploadPolygon}
        />
      )}
      {actions.includes("export") && canViewExport && (
        <StackedNavigationButton
          data-testid={`export-${testPrefix}-button`}
          icon={<FileDownload />}
          title="Export Layer"
          subtitle="Download layer data"
          onClick={handleOnExportLayer}
        />
      )}
      {actions.includes("delete") && onDelete && (
        <>
          <StackedNavigationButton
            data-testid={`delete-${testPrefix}-button`}
            icon={<DeleteOutline />}
            title="Delete"
            subtitle="Remove existing data"
            onClick={handleOnDeleteButtonClick}
            disabled={!canDelete}
          />
          <ConfirmModal
            open={showDeleteConfirm}
            header={deleteConfirmHeader ?? `Delete ${title.toLowerCase()}?`}
            confirmText="Delete"
            cancelText="Cancel"
            color="error"
            content={
              deleteConfirmContent ?? (
                <Typography variant="body2">
                  This will permanently delete all existing {title.toLowerCase()} data. This action cannot be undone.
                </Typography>
              )
            }
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          />
        </>
      )}
    </Box>
  );
};

export default LayerPanel;
